import React, { useEffect, useRef, useState } from "react";
import { SPOTLIGHT_COLORS, SPOTLIGHT_FONT_FAMILY } from "./spotlightTheme";

const styles = {
	card: {
		boxSizing: "border-box",
		backgroundColor: SPOTLIGHT_COLORS.cream,
		color: SPOTLIGHT_COLORS.text,
		borderRadius: 20,
		padding: "18px 20px 16px",
		boxShadow: "0 18px 48px rgba(10, 24, 16, 0.38), 0 0 0 1px rgba(76, 149, 108, 0.25)",
		fontFamily: SPOTLIGHT_FONT_FAMILY,
	},
	header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 },
	chapterLabel: { fontSize: 12, letterSpacing: 0.8, textTransform: "uppercase", color: SPOTLIGHT_COLORS.green, fontWeight: 700 },
	previewBadge: { fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: SPOTLIGHT_COLORS.white, backgroundColor: SPOTLIGHT_COLORS.orange, borderRadius: 999, padding: "2px 8px" },
	title: { margin: "0 0 6px", fontSize: 22, lineHeight: 1.2, color: SPOTLIGHT_COLORS.greenDark, fontWeight: 700 },
	body: { margin: 0, fontSize: 15, lineHeight: 1.5 },
	bulletList: { margin: "10px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 },
	bulletItem: { fontSize: 14, lineHeight: 1.45, padding: "8px 10px", borderRadius: 12, backgroundColor: SPOTLIGHT_COLORS.greenLight },
	bulletLabel: { fontWeight: 700, color: SPOTLIGHT_COLORS.greenDark },
	hint: { margin: "10px 0 0", fontSize: 13, lineHeight: 1.4, color: SPOTLIGHT_COLORS.orange, fontWeight: 700 },
	dots: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 5, marginTop: 14 },
	footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 14 },
	footerActions: { display: "flex", alignItems: "center", gap: 8 },
	linkButton: { background: "none", border: "none", padding: 4, margin: 0, fontFamily: SPOTLIGHT_FONT_FAMILY, fontSize: 13, color: SPOTLIGHT_COLORS.mutedText, textDecoration: "underline" },
	backButton: { background: "transparent", border: `1px solid ${SPOTLIGHT_COLORS.green}`, color: SPOTLIGHT_COLORS.greenDark, borderRadius: 999, padding: "9px 16px", fontFamily: SPOTLIGHT_FONT_FAMILY, fontSize: 14, fontWeight: 700 },
	primaryButton: { backgroundColor: SPOTLIGHT_COLORS.green, color: SPOTLIGHT_COLORS.white, border: "none", borderRadius: 999, padding: "10px 20px", fontFamily: SPOTLIGHT_FONT_FAMILY, fontSize: 14, fontWeight: 700 },
	primaryButtonDisabled: { backgroundColor: "#b9c9bf", color: "#eef3ef" },
	skipConfirm: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13 },
	pill: {
		boxSizing: "border-box",
		display: "flex",
		alignItems: "center",
		gap: 10,
		backgroundColor: SPOTLIGHT_COLORS.cream,
		borderRadius: 999,
		padding: "10px 12px 10px 16px",
		boxShadow: "0 12px 32px rgba(10, 24, 16, 0.38), 0 0 0 1px rgba(76, 149, 108, 0.3)",
		fontFamily: SPOTLIGHT_FONT_FAMILY,
		color: SPOTLIGHT_COLORS.greenDark,
	},
	pillDot: { flexShrink: 0, width: 10, height: 10, borderRadius: 5, backgroundColor: SPOTLIGHT_COLORS.orange, animation: "spotlightDotPulse 1.4s ease-in-out infinite" },
	pillText: { fontSize: 15, fontWeight: 700, lineHeight: 1.3 },
	pillStepButton: { backgroundColor: SPOTLIGHT_COLORS.green, color: SPOTLIGHT_COLORS.white, border: "none", borderRadius: 999, padding: "6px 14px", fontFamily: SPOTLIGHT_FONT_FAMILY, fontSize: 13, fontWeight: 700 },
};

const ProgressDots = ({ current, total }) => (
	<div style={styles.dots} aria-hidden="true">
		{Array.from({ length: total }, (_, index) => {
			const isCurrent = index + 1 === current;
			const isDone = index + 1 < current;
			return (
				<span
					key={index}
					style={{
						height: 6,
						width: isCurrent ? 18 : 6,
						borderRadius: 3,
						backgroundColor: isCurrent ? SPOTLIGHT_COLORS.orange : isDone ? SPOTLIGHT_COLORS.green : "#cfe3d6",
						transition: "width 280ms ease, background-color 280ms ease",
					}}
				/>
			);
		})}
	</div>
);

const SkipControl = ({ onSkip }) => {
	const [isConfirming, setIsConfirming] = useState(false);

	if (!isConfirming) {
		return (
			<button type="button" className="spotlight-link" style={styles.linkButton} onClick={() => setIsConfirming(true)}>
				Skip tutorial
			</button>
		);
	}

	return (
		<span style={styles.skipConfirm}>
			<span>Skip the tutorial?</span>
			<button type="button" className="spotlight-link" style={{ ...styles.linkButton, color: SPOTLIGHT_COLORS.greenDark, fontWeight: 700 }} onClick={onSkip}>
				Yes, skip
			</button>
			<button type="button" className="spotlight-link" style={styles.linkButton} onClick={() => setIsConfirming(false)}>
				Keep going
			</button>
		</span>
	);
};

export const SpotlightPill = ({ instruction, onSkip, onSkipStep, skipStepLabel = "Skip step", isPreview }) => (
	<div style={styles.pill} role="status" aria-live="polite">
		<span style={styles.pillDot} data-spotlight-animated />
		<span style={styles.pillText}>{instruction}</span>
		{isPreview && <span style={styles.previewBadge}>PREVIEW</span>}
		{onSkipStep && (
			<button type="button" className="spotlight-button" style={styles.pillStepButton} onClick={onSkipStep}>
				{skipStepLabel}
			</button>
		)}
		<SkipControl onSkip={onSkip} />
	</div>
);

export const SpotlightResumeBanner = ({ chapterTitle, progress, onResume, onSkip, isPreview }) => (
	<div style={{ ...styles.pill, position: "fixed", left: "50%", bottom: 20, transform: "translateX(-50%)", maxWidth: "calc(100vw - 24px)", flexWrap: "wrap", zIndex: 2147483000 }} role="status">
		<span style={styles.pillDot} data-spotlight-animated />
		<span style={styles.pillText}>
			{isPreview ? "Tutorial preview paused" : "Your MyTassel tutorial is paused"} · {chapterTitle} {progress.current} of {progress.total}
		</span>
		<button type="button" className="spotlight-button" style={styles.pillStepButton} onClick={onResume}>
			Continue
		</button>
		<SkipControl onSkip={onSkip} />
	</div>
);

const SpotlightCardContent = ({
	step,
	chapterTitle,
	progress,
	isPreview,
	primaryLabel,
	showPrimary,
	isPrimaryDisabled,
	hint,
	canGoBack,
	onNext,
	onBack,
	onSkip,
}) => {
	const primaryRef = useRef(null);

	useEffect(() => {
		if (!step.interactive) primaryRef.current?.focus({ preventScroll: true });
	}, [step.id]);

	return (
		<div style={{ animation: "spotlightCardIn 320ms ease both" }} data-spotlight-animated>
			<div style={styles.header}>
				<span style={styles.chapterLabel}>
					{chapterTitle} · {progress.current} of {progress.total}
				</span>
				{isPreview && <span style={styles.previewBadge}>PREVIEW</span>}
			</div>
			<h3 style={styles.title}>{step.title}</h3>
			<p style={styles.body}>{step.body}</p>
			{step.bullets && (
				<ul style={styles.bulletList}>
					{step.bullets.map((bullet) => (
						<li key={bullet.label} style={styles.bulletItem}>
							<span style={styles.bulletLabel}>{bullet.label}</span> {bullet.text}
						</li>
					))}
				</ul>
			)}
			{hint && <p style={styles.hint}>{hint}</p>}
			<ProgressDots current={progress.current} total={progress.total} />
			<div style={styles.footer}>
				<SkipControl onSkip={onSkip} />
				<div style={styles.footerActions}>
					{canGoBack && (
						<button type="button" className="spotlight-button" style={styles.backButton} onClick={onBack}>
							Back
						</button>
					)}
					{showPrimary && (
						<button
							ref={primaryRef}
							type="button"
							className="spotlight-button"
							style={{ ...styles.primaryButton, ...(isPrimaryDisabled ? styles.primaryButtonDisabled : null) }}
							disabled={isPrimaryDisabled}
							onClick={onNext}
						>
							{primaryLabel}
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export const SpotlightCard = (props) => (
	<div style={styles.card} role="dialog" aria-label={props.step.title}>
		<SpotlightCardContent key={props.step.id} {...props} />
	</div>
);
